import { create } from "zustand";
import { supabase } from "../lib/supabase";

interface CourseState {
  courses: any[];
  featuredCourses: any[];
  categories: any[];
  selectedTag: string | null;
  currentCourse: any | null;
  totalCourses: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  lastFetched: number | null;
  fetchCourses: (page?: number, force?: boolean) => Promise<void>;
  fetchFeaturedCourses: (force?: boolean) => Promise<void>;
  fetchCategories: (force?: boolean) => Promise<void>;
  fetchCourse: (identifier: string, isSlug?: boolean) => Promise<void>;
  updateCourse: (id: string, data: any) => Promise<void>;
  setSelectedTag: (tag: string | null) => void;
  clearError: () => void;
}

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000;
const REQUEST_TIMEOUT = 30000;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useCourseStore = create<CourseState>((set, get) => ({
  courses: [],
  featuredCourses: [],
  categories: [],
  selectedTag: null,
  currentCourse: null,
  totalCourses: 0,
  currentPage: 1,
  loading: false,
  error: null,
  lastFetched: null,

  fetchCourses: async (page = 1, force = false) => {
    const { lastFetched, selectedTag } = get();
    const now = Date.now();

    // Check if we have cached data and it's still fresh
    if (!force && lastFetched && now - lastFetched < CACHE_DURATION) {
      return;
    }

    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        set({ loading: true, error: null });

        let query = supabase
          .from("courses")
          .select(
            `
            *,
            course_categories (*),
            course_tags_map (
              course_tags (*)
            )
          `,
            { count: "exact" },
          )
          .eq("is_published", true)
          .eq("deleted", false)
          .order("created_at", { ascending: false });

        if (selectedTag) {
          query = query.eq("course_tags_map.course_tags.name", selectedTag);
        }

        const { data: courses, error, count } = await query;

        if (error) throw error;

        set({
          courses: courses || [],
          totalCourses: count || 0,
          currentPage: page,
          loading: false,
          lastFetched: now,
        });

        break;
      } catch (error) {
        console.warn(`Fetch attempt ${retries + 1} failed:`, error);

        if (retries === MAX_RETRIES - 1) {
          set({
            error:
              error instanceof Error ? error.message : "Failed to load courses",
            loading: false,
            courses: [],
            totalCourses: 0,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      }
    }
  },

  fetchFeaturedCourses: async (force = false) => {
    const { lastFetched } = get();
    const now = Date.now();

    // Check if we have cached data and it's still fresh
    if (!force && lastFetched && now - lastFetched < CACHE_DURATION) {
      return;
    }

    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        set({ loading: true, error: null });

        const { data: courses, error } = await supabase
          .from("courses")
          .select(
            `
            *,
            course_categories (*),
            course_tags_map (
              course_tags (*)
            )
          `,
          )
          .eq("is_published", true)
          .eq("is_featured", true)
          .eq("deleted", false)
          .order("created_at", { ascending: false });

        if (error) throw error;

        set({
          featuredCourses: courses || [],
          loading: false,
          lastFetched: now,
        });

        break;
      } catch (error) {
        console.warn(`Featured fetch attempt ${retries + 1} failed:`, error);

        if (retries === MAX_RETRIES - 1) {
          set({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load featured courses",
            loading: false,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      }
    }
  },

  fetchCategories: async (force = false) => {
    const { lastFetched } = get();
    const now = Date.now();

    // Check if we have cached data and it's still fresh
    if (!force && lastFetched && now - lastFetched < CACHE_DURATION) {
      return;
    }

    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        const { data: categories, error } = await supabase
          .from("course_categories")
          .select("*")
          .order("name");

        if (error) throw error;

        set({ categories: categories || [], lastFetched: now });
        break;
      } catch (error) {
        console.warn(`Categories fetch attempt ${retries + 1} failed:`, error);

        if (retries === MAX_RETRIES - 1) {
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      }
    }
  },

  fetchCourse: async (identifier: string, isSlug = true) => {
    let retries = 0;

    while (retries < MAX_RETRIES) {
      try {
        set({ loading: true, error: null });

        // First fetch the course with basic info and tags
        const { data: course, error: courseError } = await supabase
          .from("courses")
          .select(
            `
            *,
            course_categories (*),
            course_tags_map (
              course_tags (*)
            )
          `,
          )
          .eq(isSlug ? "slug" : "id", identifier)
          .eq("deleted", false)
          .single();

        if (courseError) {
          if (courseError.code === "PGRST116") {
            throw new Error("Course not found");
          }
          throw courseError;
        }

        if (!course) {
          throw new Error("Course not found");
        }

        // Then fetch modules with topics
        const { data: modules, error: modulesError } = await supabase
          .from("course_modules")
          .select(
            `
            *,
            module_topics (*)
          `,
          )
          .eq("course_id", course.id)
          .order("order_number");

        if (modulesError) throw modulesError;

        // Fetch learning outcomes
        const { data: outcomes, error: outcomesError } = await supabase
          .from("course_learning_outcomes")
          .select("*")
          .eq("course_id", course.id)
          .order("order_number");

        if (outcomesError) throw outcomesError;

        // Fetch course sessions
        const { data: sessions, error: sessionsError } = await supabase
          .from("course_sessions")
          .select("*")
          .eq("course_id", course.id)
          .order("session_date");

        if (sessionsError) throw sessionsError;

        // Process and combine all data
        const processedCourse = {
          ...course,
          selectedTags:
            course.course_tags_map?.map(
              (tagMap: any) => tagMap.course_tags.id,
            ) || [],
          course_modules:
            modules
              ?.map((module) => ({
                ...module,
                module_topics: module.module_topics?.sort(
                  (a: any, b: any) => a.order_number - b.order_number,
                ),
              }))
              .sort((a: any, b: any) => a.order_number - b.order_number) || [],
          course_learning_outcomes:
            outcomes?.sort(
              (a: any, b: any) => a.order_number - b.order_number,
            ) || [],
          course_sessions: sessions || [],
        };

        set({
          currentCourse: processedCourse,
          loading: false,
        });

        break;
      } catch (error) {
        console.warn(`Course fetch attempt ${retries + 1} failed:`, error);

        if (retries === MAX_RETRIES - 1) {
          set({
            error:
              error instanceof Error ? error.message : "Failed to load course",
            loading: false,
            currentCourse: null,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      }
    }
  },

  updateCourse: async (id: string, data: any) => {
    let retries = 0;

    while (retries < MAX_RETRIES) {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

      try {
        set({ loading: true, error: null });

        // Extract tags from data
        const { tags, ...courseData } = data;

        // Update course data
        const { error: courseError } = await supabase
          .from("courses")
          .update(courseData)
          .eq("id", id)
          .abortSignal(controller.signal);

        if (courseError) throw courseError;

        // Update tags if provided
        if (tags !== undefined) {
          // Delete existing tag mappings
          const { error: deleteError } = await supabase
            .from("course_tags_map")
            .delete()
            .eq("course_id", id)
            .abortSignal(controller.signal);

          if (deleteError) throw deleteError;

          // Insert new tag mappings if there are any
          if (tags && tags.length > 0) {
            const tagMappings = tags.map((tagId: string) => ({
              course_id: id,
              tag_id: tagId,
            }));

            const { error: insertError } = await supabase
              .from("course_tags_map")
              .insert(tagMappings)
              .abortSignal(controller.signal);

            if (insertError) throw insertError;
          }
        }

        // Refresh course data
        await get().fetchCourse(id, false);
        set({ loading: false });

        break;
      } catch (error) {
        console.warn(`Course update attempt ${retries + 1} failed:`, error);

        if (error instanceof Error && error.name === "AbortError") {
          set({
            error: "Update timed out. Please try again.",
            loading: false,
          });
          break;
        }

        if (retries === MAX_RETRIES - 1) {
          set({
            error:
              error instanceof Error ? error.message : "Failed to save course",
            loading: false,
          });
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
        retries++;
      } finally {
        clearTimeout(timeoutId);
      }
    }
  },

  setSelectedTag: (tag: string | null) => {
    set({ selectedTag: tag });
  },

  clearError: () => set({ error: null }),
}));
